// Client mirror of functions-common/src/charts/engine/compare.ts. Keep in sync.

import type { ChartFilter, ChartMeasure, ChartRange } from '../types.js'
import {
  shiftCalendarDays,
  shiftCalendarMonths,
  toDate,
} from './bucket.js'
import {
  matchFilter,
  resolveAbsoluteWindow,
  resolveRelativeWindow,
} from './filter.js'
import { computeAggregate } from './aggregate.js'

export function computePreviousPeriod(
  rows: Array<Record<string, unknown>>,
  filters: ChartFilter[],
  range: ChartRange,
  timeField: string | undefined,
  measure: ChartMeasure,
  nowDate: Date,
  timeZone: string = 'UTC'
): number | undefined {
  if (!timeField || range.kind === 'all') return undefined

  let prevWindow: { from: Date; to: Date } | null = null

  if (range.kind === 'relative') {
    const currentWindow = resolveRelativeWindow(range.last, range.unit, nowDate, timeZone)
    if (range.unit === 'month') {
      prevWindow = {
        from: shiftCalendarMonths(currentWindow.from, -range.last, timeZone),
        to: shiftCalendarMonths(currentWindow.to, -range.last, timeZone),
      }
    } else if (range.unit === 'week') {
      const days = range.last * 7
      prevWindow = {
        from: shiftCalendarDays(currentWindow.from, -days, timeZone),
        to: shiftCalendarDays(currentWindow.to, -days, timeZone),
      }
    } else {
      // day
      prevWindow = {
        from: shiftCalendarDays(currentWindow.from, -range.last, timeZone),
        to: shiftCalendarDays(currentWindow.to, -range.last, timeZone),
      }
    }
  } else if (range.kind === 'absolute') {
    const currentWindow = resolveAbsoluteWindow(range.from, range.to, timeZone)
    if (currentWindow) {
      const fMatch = range.from.match(/^(\d{4})-(\d{2})-(\d{2})/)
      const tMatch = range.to.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (fMatch && tMatch) {
        const fy = Number(fMatch[1])
        const fm = Number(fMatch[2])
        const fd = Number(fMatch[3])
        const ty = Number(tMatch[1])
        const tm = Number(tMatch[2])
        const td = Number(tMatch[3])
        const inclusiveDays =
          Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000) + 1
        prevWindow = {
          from: shiftCalendarDays(currentWindow.from, -inclusiveDays, timeZone),
          to: shiftCalendarDays(currentWindow.to, -inclusiveDays, timeZone),
        }
      }
    }
  }

  if (!prevWindow) return undefined

  const prevRows = rows.filter((row) => {
    // 1. Filters
    for (const filter of filters) {
      if (!matchFilter(row[filter.field], filter)) return false
    }

    // 2. Previous window range
    const rowDate = toDate(row[timeField])
    if (!rowDate) return false
    const ms = rowDate.getTime()
    return ms >= prevWindow!.from.getTime() && ms <= prevWindow!.to.getTime()
  })

  return computeAggregate(prevRows, measure)
}
